import { getPersona } from "../shared/brand/persona";

/**
 * 更新记录(契约 §21.3)—— **最新一条在最前,它的 version 就是当前构建的版本**。
 *
 * ★为什么版本号写在这个文件里,而不是 vite define / CI 注入:
 *   这份清单**随构建一起发**(改它就得重新打包),所以「文件里的第一条」与「用户手上跑的这版」
 *   天然同步。走 define/环境变量反而多一条会漂的线 —— 版本号对了但清单还是旧的,或者反过来,
 *   而两种漂移在界面上都长得像「更新记录没更新」。顺带:不改 vite.config。
 *
 * ★维护规矩:发版时**在数组最前面加一条**,不要改老条目(老条目是已经发出去的承诺)。
 *   `WhatsNewDialog` 只认第一条的 `version`,加在中间等于这次更新不弹。
 *
 * ★`kind`(0925 版本号规划拍板):`minor` = 对外里程碑,`shouldShowWhatsNew`
 *   (见 `releaseVersion.ts`)据此决定要不要弹「本次更新」;`patch` = 缺陷/欠账/小功能,
 *   changelog 页照常列出全部条目,只是标一个「补丁」小标、不弹框。判弹不弹的真正依据是
 *   版本号本身跨不跨 MINOR(`minorOf`),`kind` 只管 changelog 页面上的展示标签,别在别处拿它当判据用。
 */
export interface ReleaseNoteItem {
  /** 归哪个舱 / 哪一类(自由文本,按舱名写;非舱的横切项用「客服口径」这类)。 */
  cabin: string;
  text: string;
}

export interface ReleaseNote {
  /** 语义版本号,非空。 */
  version: string;
  /** 发布日期 YYYY-MM-DD。 */
  date: string;
  /** "minor" = 对外里程碑(老板拍板才升);"patch" = 缺陷/欠账/小功能(发版车自行递增)。 */
  kind: "minor" | "patch";
  items: ReleaseNoteItem[];
}

export const releaseNotes: ReleaseNote[] = [
  {
    version: "1.2.7",
    date: "2026-10-27",
    kind: "patch",
    items: [
      {
        cabin: "获客舱",
        text: "商机按归属分开看:谁点「发现一次」找到的商机就归谁;管理员看全部,经理看自己分管的部门,员工看自己名下的。之前的老商机和夜里自动找到的放进「公共池」,谁先认领归谁。今日商机多了「全部 / 只看我的 / 公共池」三档;找到的企业已经在同事名下时,只告诉你有几家,不重复给你。",
      },
      {
        cabin: "获客舱",
        text: "评论线找客户时,会按你自己写的话题词和客户常问来判断是不是潜在客户,不再只看行业词;话题词没填或删光时会提醒你。",
      },
      {
        cabin: "获客舱",
        text: "地图获客的搜索结果显示已找到多少家、还有没有更多,翻到底显示总数;「我的线索」显示一共多少条。",
      },
      {
        cabin: "分身舱",
        text: "用「我的声纹」选了英语、韩语等外语,稿子却还是中文时,口播框下会提醒并给「一键翻译」:译文直接填进输入框,看过再出片,不满意可以改或还原中文稿;译文里和原文对不上的数字会标出来请你核对。",
      },
      {
        cabin: "分身舱",
        text: "上传真人出镜视频时,提示的最长时长和实际上限一致(现在是 5 分钟)。",
      },
      {
        cabin: "视频舱",
        text: "成片里不会再把公司名当成产品品牌烧成大字;品牌字只从你的产品资料里取。",
      },
    ],
  },
  {
    version: "1.2.6",
    date: "2026-10-27",
    kind: "patch",
    items: [
      {
        cabin: "视觉舱",
        text: "一键长图点「改这一稿」:左边按屏分卡、带缩略图,和右边成品一屏对一屏;改了字的屏左右都标「已改」,右边仍显示原图,出图时整屏按新内容重画。重新出图保持原来的分屏,按屏报价、扣费与报价一致;字加多的屏会按新内容重新定画幅,也可以在屏卡上手选画幅。",
      },
      {
        cabin: "行业知识库",
        text: "新增 13 个行业:建材、家具家居、服装鞋服、汽车后市场、化工新材料、农资与种养殖、养生康养服务、文旅、医疗机构、日化个护、家电厨卫、体育户外、宠物,并新开「汽车与出行」「农林牧渔」两个大类;节能环保、公益慈善、大健康食品、医药制造、智能家居、大数据、动漫设计、宠物服务与连锁加盟、品牌运营服务 9 个行业的资料也一并补齐。选好行业后,写稿、海报、客服、陪练都会按这一行的说法和红线来。",
      },
      {
        cabin: "行业知识库",
        text: "食品酒水茶、餐饮、工业设备与自动化、美业、大健康食品、保健品、医疗器械、节能环保、SaaS、职业教育、K12、家装维修、法律财税、知识产权这些行业的资料,按真实客户的产品、问答和案例重新写过,话术、选题和客服应答更贴近这一行的实际生意。",
      },
      {
        cabin: "获客舱",
        text: "新增的行业开箱就带行业找客户的关键词和渠道配置,不用自己从头想词就能开始发现商机。",
      },
      {
        cabin: "陪练舱",
        text: "「汽车与出行」「农林牧渔」两个新大类各自带上贴合行业的预置陪练场景,大类下的细分行业自动可用。",
      },
      {
        cabin: "帮助中心",
        text: "行业篇新增建材、汽车后市场、文旅、医疗机构等 20 个行业,原有行业篇按新资料全部更新,现在覆盖 42 个行业和通用篇。",
      },
    ],
  },
  {
    version: "1.2.5",
    date: "2026-10-25",
    kind: "patch",
    items: [
      {
        cabin: "陪练舱",
        text: "场景里的客户声音可以选分身舱「我的音色」,选了之后方言、说话风格才放开并真正生效;语音页客户头像可设男 / 女(默认跟随音色)。本场没涉及到的评分维度显示「未涉及」、不再按 0 分拉低总分;AI 客户的回复不再夹带模型自己续写的多轮对话。",
      },
      {
        cabin: "陪练舱",
        text: "评估 PDF 改版:员工、部门、任务、场景、练习时间与及格线,加成绩卡、能力雷达、维度得分、逐轮点评、同场景历次记录和对话附录,文件名带员工名和日期;分数配色和合格率按任务自己的及格线算;首页「高风险员工」按人统计(看每人最近一次),不再按不及格次数;草稿和已归档场景不再出现在员工列表和派任务下拉里;截止时间按本地时间保存,不再差 8 小时。",
      },
      {
        cabin: "获客舱",
        text: "今日商机满额后「发现一次」不再变灰,新找到的直接进线索库;点「成了 / 没成」后卡片自动离开今日页、下一张补上,不用手动刷新;接手过的商机移到线索库跟进,不再占今日名额;评论线话题词最多 5 个,超出的老词会划掉提示。",
      },
      {
        cabin: "视频舱",
        text: "续渲、单段重渲和合成成片沿用第一次的分段,口播和字幕不再错位、不再漏镜;同一位人物换机位时延续上一段的形象,不再中途换人;用海报做首帧时,隔久了再出片也不会因图片链接过期而失败。",
      },
      {
        cabin: "新闻舱",
        text: "「转社交种草长图」会把文章带进长图备料框,不再进去一片空白;发布到官网时配图自动压缩,大图不再导致发布失败。",
      },
      {
        cabin: "建站舱",
        text: "模板自带的示例图只用在装饰和图标位,其余图片位用 AI 配图或本站已有的图,不再把别家门头挂到你的站上;AI 改写导航菜单时不会和已有菜单重名。",
      },
      {
        cabin: "客服舱",
        text: "知识库里没有的内容,AI 客服不再替商家做承诺;这类问题(包括回答里说「资料中暂未提到」的)都会记进未命中清单,方便补资料。",
      },
      {
        cabin: "知识库",
        text: "把文档设为对客可见需要另一位管理员放开时,提示会直接说明,不再让你「重试」。",
      },
    ],
  },
  {
    version: "1.2.4",
    date: "2026-10-22",
    kind: "patch",
    items: [
      {
        cabin: "获客舱",
        text: "评论线的「地区」开始生效:按评论者的 IP 属地筛,精确到省(西瓜视频没有属地信息,抽屉里如实说明);招投标线按地区检索并按采购方所在地筛选。",
      },
      {
        cabin: "出图",
        text: "图改图恢复 2K / 4K 可选。",
      },
      {
        cabin: "长图",
        text: "正文 / 引用块里填的「副文本」会画到成品上(小一号、紧跟正文),预览里也看得到。",
      },
      {
        cabin: "陪练舱",
        text: "场景音色试听恢复正常;家装维修行业不再带出本地生活的到店陪练预置场景。",
      },
      {
        cabin: "新闻舱",
        text: "稿件超字数需要压缩时,结尾的行动号召和品牌落点最后才动,不再被先裁掉。",
      },
    ],
  },
  {
    version: "1.2.3",
    date: "2026-10-20",
    kind: "patch",
    items: [
      {
        cabin: "追爆舱",
        text: "发现区改版:爆款列表可按综合、点赞最多、最新发布、点赞率排序,支持六档时长筛选,卡片显示点赞率和热度指数;爆款列表可手动刷新(冷却期内倒数),刷新和换时长 11 积分 / 次,不与换词费叠加,扣费前先确认;分不进组的关联词折叠成「其他 N 个」。",
      },
      {
        cabin: "追爆舱",
        text: "新增话题订阅:订阅当前话题词不扣分,每天自动刷新爆款列表(每个词每天 11 积分,余额不足当天不更新),有新作品时亮红点,打开即标为已看。",
      },
      {
        cabin: "获客舱",
        text: "企业库找客户改为「按关键词找,行业、地区缩小范围」,关键词必填,企业画像会预填关键词。",
      },
      {
        cabin: "推广舱",
        text: "还没开通 GEO 服务的企业,点推广舱能看到服务介绍页,不再只有一句权限提示。",
      },
      {
        cabin: "分身舱",
        text: "成片「调版式」画布不再被挤到下半屏;叠 logo / 二维码重合成时人物画面不再被压扁,logo、二维码按原图比例显示;文案字数上限和上传视频时长按实际出片能力显示。",
      },
      {
        cabin: "出图",
        text: "图改图页面重排,两处「参考图」分清各自用途。",
      },
      {
        cabin: "GEO雷达",
        text: "联网实测恢复,4 家实测平台都能正常出结果。",
      },
      {
        cabin: "知识库",
        text: "上传时文档类型不对会当场说明原因,不再生成一份无法使用的草稿。",
      },
    ],
  },
  {
    version: "1.2.2",
    date: "2026-10-20",
    kind: "patch",
    items: [
      {
        cabin: "出图",
        text: "选产品出图时,产品参数默认不再画到图上,尺寸只用来约束画面比例,不再出现标注数字。",
      },
      {
        cabin: "AI 换衣",
        text: "换用新的编辑引擎,挂拍图、人台图、买家秀都能上传(只取衣服),价格不变,仍为 80 积分 / 张。",
      },
      {
        cabin: "分身舱",
        text: "照片省流版正式下架,相关入口已移除。",
      },
    ],
  },
  {
    version: "1.2.1",
    date: "2026-10-18",
    kind: "patch",
    items: [
      {
        cabin: "获客舱",
        text: "新增「企业库找客户」:按关键词、行业、地区、标签筛企业,支持名称联想,勾选后直接入线索库;线索卡新增企业画像,可补全联系方式(50~120 积分 / 条,补全前显示价格),线索库支持批量补全。",
      },
      {
        cabin: "获客舱",
        text: "一轮发现只冻结一笔积分,没出卡当场退回;政府公示线恢复出卡,同一页面不再重复出卡;处罚线主体名更干净、只看近 90 天;西瓜视频线不再挑到没有评论的视频;专精特新、处罚线只用行业词检索;夜间自动发现与手动发现共用同一套去重。",
      },
      {
        cabin: "视频舱",
        text: "真人出镜「文案出片」新增「对镜说话 / 旁白」开关;从外部粘贴的口播稿会去掉「口播内容提炼如下:」这类开场白,不再被念出来;导演交代卡显示审片时发现缺的素材;「从产品库开始」进创建页直接弹出产品库;选人物库人物出片时不再被截短,产品和场景参考不再丢失;较长的视频出片等待时间放宽到 30 分钟,不再到 10 分钟就判失败。",
      },
      {
        cabin: "出图",
        text: "海报详情「让这张图动起来」改为下拉按钮,可选出片方式;多图参考可以随草稿保存和恢复;商品主图不再自己画上「私信咨询」;组图不再多出台本里没有的小标语;AI 重画后仍保留证书、无二维码等画法要求;AI 补足生成中关掉窗口不丢单,可在后台继续,产品物料上显示「生成中」。",
      },
      {
        cabin: "长图",
        text: "全部是图片块时出图前会提醒「成品将没有任何文字」;屏幕底部的文字不再被误裁;结尾屏的品牌口号保留在图上;没有二维码时只删「扫码」引导,不再改写成「私信咨询」。",
      },
      {
        cabin: "分身舱",
        text: "失败的作品卡可以直接重试(被审核拦下的不提供重试,避免再扣一次);人物库可以删除人物;出错时统一给中文说明。",
      },
      {
        cabin: "客服舱",
        text: "访客说「我要人工」时真正转接,只回一句转接提示;AI 临时出错时不再断开会话;控制台测试对话能连续问,不再每问新开一场。",
      },
      {
        cabin: "建站舱",
        text: "接入 AI 客服后官网右下角自动挂上客服浮窗;已上线的站点可以直接删除(立即下线);案例配图、资质证书可以上传;绑定域名第 2 步写清楚要去 DNS 控制台添加记录;工业类企业的按钮不再被写成「预约到店」。",
      },
      {
        cabin: "新闻舱",
        text: "重写失败时工作台给出说明;卡面报价改为整篇口径(整理 + 规划 + 正文),约 10~20 积分 / 篇。",
      },
      {
        cabin: "追爆舱",
        text: "「换一个话题词」调整为 25 积分 / 次。",
      },
      {
        get cabin() {
          return `${getPersona().title}`;
        },
        get text() {
          return `后来新建或设为管理员的账号也默认开通${getPersona().title}授权;问建站相关问题时按本企业建站舱的实际开通状态回答,不再一律说「即将上线」;问「某功能什么时候能用」不再被当成查任务进度;不会把闲置很久的旧话题翻出来;已经认识企业库、官网发布、GEO 4 家实测、评论获客和真人出镜「对镜说话 / 旁白」等新功能。`;
        },
      },
      {
        cabin: "知识库",
        text: "带密码的 Word 文档会提示「文件已加密」,不再报文件损坏;个人文档只有本人能删除,管理员不能删同事的私人文档。",
      },
      {
        cabin: "GEO雷达",
        text: "品类词按行业理解,比如「IP 确权」不再被误当成网络代理 IP。",
      },
      {
        cabin: "推广舱",
        text: "已开通 GEO 服务的企业,点推广舱卡直接免密进入 GEO 管理系统。",
      },
      {
        cabin: "系统",
        text: "上传图片识别恢复正常;AI 主通道繁忙时自动切换备用通道,陪练、客服等不再整段不可用;首页「进行中」补上一键长图排版阶段的任务;登录频率限制只按失败次数计,正常登录不再被误拦。",
      },
      {
        cabin: "陪练舱",
        text: "语音陪练拿不到麦克风时,提示先检查企业微信的系统麦克风权限。",
      },
      {
        cabin: "平台管理",
        text: "渠道管理员登错入口时提示正确的登录地址;审计时间按服务器时钟记录;被人工驳回的作品在下载、补足、封面等所有地方都隐藏;审核台命中词显示更清楚;旧月结单登记打款时金额按分取整。",
      },
    ],
  },
  {
    version: "1.2.0",
    date: "2026-10-12",
    kind: "minor",
    items: [
      {
        cabin: "获客舱",
        text: "评论获客全面改版:抖音、小红书、快手、B 站评论线改为「按话题词找视频 → 翻页读评论 → 判断求购意向」;推荐页可以自己编辑话题词和「客户常问」;商机卡上显示命中的话题词,证据里分清哪段是视频标题、哪段是评论原文;点「私信」直接跳到对方主页。",
      },
      {
        cabin: "获客舱",
        text: "新增「西瓜视频评论意向」渠道,每轮 80 积分;抖音、小红书评论线可以指定竞品账号或指定视频 / 笔记,专盯对手评论区里的意向客户。",
      },
      {
        cabin: "获客舱",
        text: "一轮最多出 30 张商机卡(原 10 张),不再有「今日商机已满」限制,判中的都出卡、多的进线索库;抖音评论、小红书评论常驻主推;招投标线只找招标公告,不再混进中标结果;再次发现同一条线索时会刷新联系方式;商机卡金额去掉多余的零(「95.000000万元」改为「95万元」),平台热线不会再被当成联系人电话。",
      },
      {
        cabin: "追爆舱",
        text: "发现区新增「抖音热榜 · 免费」:六个榜单,同城榜可选城市,不扣积分;解析支持西瓜视频、今日头条视频链接(含短链),头条图文会提示暂不支持;卡面上限统一写成「解析部分最高 400,取片另计」,近 30 天爆款数被截断时显示「1,000+」。",
      },
      {
        cabin: "追爆舱",
        text: "爆款「怎么借」:拆解后可以免费做逐句分析;交给数字人时逐句对照改写,照搬参考片的承诺、产品名会标黄,并在出片前提醒;交给新闻舱时可以选择引用其中的原话(默认只勾开头的钩子句),稿件里以引用方式呈现。",
      },
      {
        cabin: "视频舱",
        text: "口播超长时实时显示字数,逐句列出会被截掉的原文,提交前再提醒一次,截断按整句来;字幕按实际念出的语音对时间轴,引号、换行、数字和英文不再把字幕切坏;多镜成片配音逐镜对齐;旁白镜里的人物不再对口型。编排报价改为「按实际用量,通常 100~500,最高 1,900」;手选文生模型时不出台本图、不扣台本图费;上传图片识别不再扣积分;草稿标题优先用你填的任务名。",
      },
      {
        cabin: "出图",
        text: "按实际交付的清晰度结算:选 2K 实际出 1K 就按 1K 收,作品卡如实显示「1K(所选 2K)」;组图「一次全出 N 张」先显示整组总价再确认;已叠过 logo / 二维码的图再改图、重画不会出现两个 logo;生图默认不再自动勾选 logo 和二维码;证书类素材只画封面不编正文;产品图 AI 补足可以指定一张底图,多角度图的背面不再编造文字、钢印、标志;下载文件后缀按实际格式命名。",
      },
      {
        cabin: "长图",
        text: "用途卡写明「每屏 28 积分起(1K)」;好评按原话逐条出卡,不改写、不编署名;区块名、画面描述不再印到成品上;一键长图统一命名为「一键长图 · 产品名 · 时间」;同一份长图每屏锁定同一件产品;图片块单独出图,清晰度与海报单图一致,清晰度档以你选的为准。",
      },
      {
        cabin: "新闻舱",
        text: "文章可以直接发布到自己的官网:立即 / 定时发布、改稿后同步、下线、删除,列表里看得到官网状态(需已开通建站舱并有上线的站点);新增「官网栏目」:设好栏目和更新频率,系统自动滚动选题、提前写好稿,你确认后按计划发布,首页提醒当天待确认的稿。",
      },
      {
        cabin: "新闻舱",
        text: "成稿不再混入素材以外的行业套话、内部说法和写给编辑的说明,你指定的格式优先;改写某一段时不再搬用其他段的句子;质量报告不再误报正文里没有的内容;整理报价改为「约 3~6 积分 / 次(按实际用量)」。",
      },
      {
        cabin: "建站舱",
        text: "建站改为三步:一键生成(读资料 → 写文案 → AI 配图 → 出预览)、预览调整(按建议补充、换图或单张重新生成,正式上线前可换模板)、上线(可开启新闻自动更新、官网 AI 客服);AI 配图按张计费,28 积分 / 张,生成前显示报价。",
      },
      {
        cabin: "建站舱",
        text: "编辑页可以改全部图片位、品牌配色、导航与按钮文字(AI 按你的业务改写)、产品价格与多图;模板卡显示演示站缩略图,编辑页可以直接预览线上站点;绑定自己的域名会先检测解析,通过后自动签发证书,签发失败会说明原因并可重试。",
      },
      {
        cabin: "客服舱",
        text: "新增「网页 / H5」渠道:在「渠道管理 → 网页 / H5」建接入点,一个接入点同时给你链接、二维码和官网嵌入代码,不绑企业微信也能用 AI 客服;访客要人工时客服舱亮红点、顶栏铃铛提醒,点「接手」就能直接回复;访客留下的联系方式自动进获客舱。场景偏好显示中文名;企微渠道未绑定时会明确提示并给出「去绑定」入口。",
      },
      {
        cabin: "陪练舱",
        text: "情景对练里,客户会围绕企业知识库里的业务和产品追问,每场随机;场景难度、任务状态显示中文;未绑定企业微信时显著提示需要先绑定,派任务的提示按是否已绑定如实说明。",
      },
      {
        cabin: "分身舱",
        text: "分身素材与真人出镜素材分开存放,不再混用、互相顶替;声音墙把声纹和内置声音分两组,同名声纹带录制日期;没选演绎方式时按原声合成,克隆出来更像本人;下载文件用作品名命名,作品卡补上档位和时长。",
      },
      {
        cabin: "知识库",
        text: "企业里只有一位管理员时,可以直接审批自己提交的文档和产品提名;产品参数、属性改为按行填写,不用再手写 JSON;待审或被驳回的文档不会进入新闻舱写稿和陪练出题。",
      },
      {
        cabin: "GEO雷达",
        text: "联网实测的 AI 平台从 2 家扩到 4 家,另 4 家为推演,舱卡如实写成「4 家实测 + 4 家推演」;本品牌没被提及时显示「未被提及」,变化为 0 时显示「持平」;导出 PDF 不再出现按钮和半页空白。",
      },
      {
        get cabin() {
          return `${getPersona().title}`;
        },
        get text() {
          return `新开户的第一位管理员默认开通${getPersona().title}授权;没有授权时管理员可直接去用户管理开通,员工提示联系管理员;报价回答更准,问「多少钱」会结合上一条回复去查;可以回答「我那份资料哪天上传的」;产品库为空时引导直接做或去提名;经营诊断报告不再露出代码字样。`;
        },
      },
      {
        cabin: "外部工具",
        text: "新增「GEO管理系统」卡:已开通 GEO 服务的企业,员工在「获客舱 → 外部工具」点卡即可免密进入 GEO 后台。",
      },
      {
        cabin: "套餐与计费",
        text: "套餐到期后显示「已到期」,不再显示「未开通」;积分明细导出的「说明」列显示套餐中文名和周期;经营诊断在积分明细、用量和团队看板里单独成项;增值舱里还没上线的舱写「即将上线」。",
      },
      {
        cabin: "账号安全",
        text: "登录会话在使用中自动续期,活跃时不再到 24 小时被强制退出。",
      },
      {
        cabin: "系统",
        text: "首页问候按姓名、企业名称呼,不再叫邮箱;对客页面去掉技术用语,「客户成功经理」统一改称「客户经理」;「我的作品」里进行中的卡也显示场景标签和创建人。",
      },
      {
        cabin: "平台管理",
        text: "租户详情新增「GEO 管理系统」卡(填 GEO 账号手机号,一键校验开通)和「合规配置」卡;新增生成内容审核台、敏感词库、全局合规配置(默认全关,不影响现有租户);代客登录任何租户都必须填写原因(至少 4 个字)并写进审计。",
      },
      {
        cabin: "渠道后台",
        text: "代客登录必须填写原因(至少 4 个字);「购买周期数」当场换算成时长显示。",
      },
    ],
  },
  {
    version: "1.1.7",
    date: "2026-10-05",
    kind: "patch",
    items: [
      {
        cabin: "获客舱",
        text: "每日商机推送按各企业设定的钟点发送,选 8 点、10 点、11 点的企业以前收不到,现在能收到了,同一天不会重复推;商机卡分开写「N 天前发生」和「MM-DD 发现」,按日历天计算;阶段标签「刚发现」改为「待初筛」;和你的产品线明显对不上的线索不再进入今日商机。",
      },
      {
        cabin: "追爆舱",
        text: "发现区拿到旧数据时会每隔几秒自动刷新,不用手动刷新页面;页头显示「MM-DD HH:mm 更新」,条数标签写明时间范围;关联词分别标出本周、上周、往期热词。",
      },
      {
        cabin: "视频舱",
        text: "自由创作选 30 秒或 1080P 时,如果系统自动升了档,会说明原因并同时列出两档报价;手动选档时每档标出「约 N 积分 / 秒」;单镜重渲、续渲在发起时先按段秒数 × 牌价报价;视频风格按企业所在行业推荐;下载的视频文件名用作品名,「我的作品」的视频预览可以直接下载。",
      },
      {
        cabin: "出图",
        text: "底栏改为「马上出图 / 定时出图」,预计耗时如实写「1–2 分钟」;作品名和下载文件名优先用产品名;品牌库里没有二维码时图上不再画假二维码、不写「扫码」;海报和长图会避开行业禁用的元素和字样;没开通视觉舱时打开页面就提示「未开通」。",
      },
      {
        cabin: "长图",
        text: "价格统一写成「按屏计价 · 每屏 N 积分」,卡墙、按钮、备料卡、失败重试都用这一种写法;用途卡的价格改按屏数算;备料页选的清晰度档在智能编排时一起带过去;企业介绍、商品详情长图只用你自己的资料和产品库。",
      },
      {
        cabin: "新闻舱",
        text: "品牌介绍选「公司简介 / 团队介绍 / 创始人故事 / 品牌故事」以前会建稿失败,现在能正常写;压字数时只按整句删减,不会留下半句;写作会参考行业通用知识和合规红线,本企业的价格、案例、资质仍只用你提供的素材;积分明细里的扣费项统一叫「新闻稿」。",
      },
      {
        cabin: "GEO雷达",
        text: "企微 GEO 变化提醒按同一品牌和上一轮对比,测多个品牌的企业不再误报掉档或竞品反超。",
      },
      {
        cabin: "客服舱",
        text: "还没有会话时显示三步引导:接渠道、自己试聊一句、补知识库;会话号失效时提示「会话号无效,请刷新页面后重试」。",
      },
      {
        get cabin() {
          return `${getPersona().title}`;
        },
        text: "确认卡还没答复时刷新页面,卡上的按钮会恢复;卡挂着时另提一件事,旧卡自动作废;一句话要多样东西时,暂时做不了的会明说下一步再做;说「用我本人出镜」会走人物库真人出镜;能把客服高频问题起草成知识库问答,再派成陪练题;日期说错时会顺口给出真实日期,回复先给结论。",
      },
      {
        cabin: "帮助中心",
        text: "新增行业篇,按企业所在行业显示,覆盖 22 个行业和通用篇;招投标相关内容按实际能力写成「采购意向 / 废标流标」。",
      },
      {
        cabin: "套餐与计费",
        text: "「我的套餐」不再显示内部套餐码,套餐自带的增值舱写「已包含」;客服、陪练额度改为按场计:一场最多 20 轮,静默 30 分钟算新一场,陪练每席每年 1000 场、客服每席每年 2000 场;积分明细表头显示「当前积分池余额」,时间不再带毫秒。",
      },
      {
        cabin: "账号安全",
        text: "从主站登录时会提示「请从贵司专属地址登录」并给出地址;技术支持进入你的账号前须填写原因,期间查看了哪些作品、文档、会话都会记入你的操作记录,且不能修改你的个人资料;操作记录里的动作名、操作人都显示中文。",
      },
      {
        cabin: "系统",
        text: "界面上残留的英文内部码统一换成中文。",
      },
    ],
  },
  {
    version: "1.1.6",
    date: "2026-10-02",
    kind: "patch",
    items: [
      {
        cabin: "套餐与计费",
        text: "套餐调整为五档:个人版(¥159 / 月,1 席)、企业入门版(¥959 / 季,2 席)、企业标准版(¥4990 / 年,3 席)、企业旗舰版(¥9900 / 年,10 席)、企业臻选版(¥19800 / 年,20 席),新增季付;换到席位更少的套餐时已有成员不会被锁在外面;「我的套餐」里每个功能标出「套餐含」「增值舱(到期日)」或「已被服务商暂停」。",
      },
      {
        cabin: "视频舱",
        text: "选视频模型时改为按质量分档:旗舰 / 高清 / 专业 / 标准 / 极速,新增「高清」档;真人出镜分旗舰 / 专业 / 极速 / 轻量四档;作品详情、确认卡和失败提示统一用这套档位名。",
      },
      {
        cabin: "出图",
        text: "自由创作提示词前 30 个字里有 emoji 时,以前会一直提示「AI 服务暂时不可用」,现在能正常出图。",
      },
      {
        cabin: "客服舱",
        text: "客服舱停用、暂停或过期后仍保留入口并标「已停用 · 仅人工处理」,可以继续查看会话、人工回复;企微新消息照常收下,AI 不回复、不扣积分,只给客户发一次提示语(可在设置里自己改)并转人工;客服回答会参考所属行业的标准应答,避开行业禁用说法。",
      },
      {
        cabin: "陪练舱",
        text: "修复「AI 认得出员工」开关保存后不生效的问题;场景表单去掉四个从来不起作用的输入项。",
      },
      {
        cabin: "GEO雷达",
        text: "评分新增「品牌认知」一项;出题只保留会点到品牌的问法(推荐 / 对比 / 口碑 / 本地等),不点名品牌的题不再拉低出现率;「本地」类问题只在识别出城市时才出。",
      },
      {
        cabin: "功能开通与作品",
        text: "没开通、已停用或已过期的功能,历史作品仍可以查看、下载、改名、收藏和删除,但不能重试或新建;功能开关变化后不用重新登录,最多 5 分钟自动生效;被暂停的功能提示「该功能已被服务商暂停」;工作台只显示真正开通的舱。",
      },
      {
        cabin: "账号安全",
        text: "首次登录必须改密,刷新页面也跳不过;修复改密成功后误弹「请求失败」;操作审计显示操作人姓名并标出「代客操作」,可以按成员筛选。",
      },
      {
        cabin: "行业资料",
        text: "新增通信增值与号码服务、食品饮料、大健康产品、广告传媒与代运营等行业资料,并补厚现有行业;写稿、海报、口播改写、客服、陪练评估生成的内容更贴合所属行业。",
      },
      {
        cabin: "渠道后台",
        text: "渠道上线独立后台:用自己的域名和品牌登录,管理名下租户的开户、续费、换套餐与增值舱;子账号分负责人 / 财务 / 运营三种角色;首页提醒钱包余额偏低和 30 天内到期的租户;可查看月结对账单。",
      },
      {
        cabin: "平台管理",
        text: "新增渠道商管理(可售套餐与增值舱、渠道钱包与线下收款、月结对账、解约改挂);可以给租户赠送或退款增值舱;支持重置租户管理员密码;删除租户后其手机号、邮箱可以重新开户;平台套餐支持季付。",
      },
    ],
  },
  {
    version: "1.1.5",
    date: "2026-09-28",
    kind: "patch",
    items: [
      {
        cabin: "出图",
        text: "部分海报顶边出现焦黑、撕裂的问题已修复,透明背景的素材会自动铺白底。",
      },
      {
        cabin: "分身舱",
        text: "上传真人视频建分身时,人物会正确标为「真人」;重名时自动加后缀,不再「点了没反应」;出错时提示显示在弹窗里,同一个文件可以重新选择上传。",
      },
      {
        cabin: "获客舱",
        get text() {
          return `地图获客里主推产品没填卖点时,让${getPersona().title}分析线索会报错的问题已修复。`;
        },
      },
      {
        cabin: "账号与协议",
        text: "新增《用户服务协议》《隐私政策》公开页,登录页提示登录即表示同意。",
      },
    ],
  },
  {
    version: "1.1.4",
    date: "2026-09-27",
    kind: "patch",
    items: [
      {
        get cabin() {
          return getPersona().title;
        },
        get text() {
          return `${getPersona().title}的产品知识全部按《销售手册》重新对齐:价格、时长、支持的平台、能不能做,答的和页面一样;纯问价、问规则的问题不再答非所问;出主图默认按 1K 报价(约 28 积分),想更清晰直接说 2K 或 4K;给它一条抖音/快手/小红书/视频号链接会稳定弹出带价的拆解确认卡;发起 GEO 体检、经营诊断都能直接报 888 积分。`;
        },
      },
      {
        cabin: "账号安全",
        text: "还在用通用初始密码 123456 的老账号(包括平台管理员)登录会被拒,需管理员重置;新建、导入的账号初始密码仍是 123456,首次登录必须改密;租户管理员不能重置同级管理员的密码;被踢下线时会说清原因(别处登录、被禁用、公司停用、密码被重置等);席位满员后新增、导入、企微同步都会被挡住并给出原因。",
      },
      {
        cabin: "套餐与计费",
        text: "追爆解析每单最高 400 积分(一单合计不超过 450),新闻稿正文最高 50 积分,页面价签同步注明;照片数字人「生动版」固定 168 积分/秒,「换音色」先开报价单再扣;失败重试单独结算不再重复扣;提前续费的新额度在到期日生效,当期剩余积分不清零;平台开通/续费时实付留空按套餐标价记账,填 0 必须写原因;退款后再续费不会覆盖手工调过的席位上限。",
      },
      {
        cabin: "视频舱",
        text: "首页「自由创作」卡按直发口径报价(5 秒 720P 约 475 积分,不含编排与台本图),目的卡写清「编排约 400~1900 + 成片按秒」;台本图按 2K 90 积分报价、与实扣一致,分镜预估不再重复计台本图;编排页默认时长改为「短·15s」,示例文案不能直接点付费编排;追爆发现区「拆这条」先弹确认再扣费。",
      },
      {
        cabin: "客服舱 / 陪练舱",
        text: "套餐不含客服舱、陪练舱的租户不再能从地址栏直接进入或调用这两个舱;客服会话次数用完时 AI 停止自动回复、客户消息照常落库给坐席,管理员首页出提醒;陪练建任务的成员复选框可以正常勾选与取消。",
      },
      {
        cabin: "知识库",
        text: "文档列表能看到「对客可见」徽章并可一键收回,看不到的文档下面写清客服为什么检索不到;加密 PDF、损坏文件会分别提示「文件已加密」「文件损坏」,不再显示成服务不可用;解析为空的文件换成文字版后可直接重传。",
      },
      {
        cabin: "获客舱",
        text: "「发现一次」改为后台运行,不再因为跑得久而报超时;跑完弹出命中数或 0 条的原因,切走页面回来能接上进度;同一张卡同时只跑一轮。",
      },
      {
        cabin: "长图 / 出图",
        text: "长图入口卡按 1K 默认档报价,与搭建器一致;一键长图详情「重画这一屏」会显示本次积分;一键长图卡回到搭建器时保留用途,失败态给出「重试」「打开草稿」两条出路并带价签。",
      },
      {
        cabin: "分身舱",
        text: "省流版按 3.5 积分/秒显示;清晰度选「高清 720」时底栏与详情不再显示成 480P;公共素材说明改为「授权入库后全公司共用出片」;换音色时未改选音色不能确认,避免重复扣费。",
      },
      {
        cabin: "平台管理",
        get text() {
          return `租户详情新增「调整」资源上限入口(1~100000,原因必填、进审计),资源上限显示「已用 / 上限」;新建租户预览的资源上限与开通后一致,不再显示「不限」;老租户为空的席位/${getPersona().title}/声纹上限按套餐补齐;建站舱对所有租户显示「即将上线」;全部报错提示改为人话并带排查号。`;
        },
      },
      {
        cabin: "系统",
        text: "行业通用资料库此前从未真正入库(客服的行业兜底一直没生效),本次修复后自动同步;历史「内部无限」时期的老租户额度改回集团版真值(客服 80000 / 陪练 40000 / 积分池 400000);账单流水的成员归属与模型标签修正。",
      },
    ],
  },
  {
    version: "1.1.3",
    date: "2026-09-25",
    kind: "patch",
    items: [
      {
        cabin: "视频舱",
        text: "视频详情的分段卡片上能看到这一段实际是怎么出的片;关口播、关字幕、关配乐这几个开关现在第一次出片就生效,不用等到重新合成才起作用。",
      },
      {
        cabin: "真人出镜",
        text: "「换我出镜」上传疑似微信转发过的视频会提醒你换一个原始文件,并新增「我有权使用这段视频」的确认;仿拍和真人出镜不能一起用时,会直接说明原因,不再报一句看不懂的「服务调用失败」。",
      },
      {
        cabin: "出图",
        text: "图改图最多能传 4 张参考图,产品图配一张模特图就能做试戴效果(传多张参考图时出的是 1K 画质);选到不支持局部重绘的模型时,「局部重绘」按钮会直接置灰。",
      },
      {
        cabin: "长图",
        text: "长图排版更稳,内容和配图多的时候不容易再遇到「一张图放不下」;报价改成按实际出的屏数算,少出的屏、被自动降档的屏都会自动退差价。",
      },
      {
        cabin: "获客舱",
        text: "获客抓取这一轮如果结果是 0,会告诉你实际抓到了多少篇、为什么被判成同行内容;关键词跟你在售的产品对不上时也会提示,不会默默不生效。",
      },
      {
        cabin: "客服口径",
        text: "客服找不到你店里资料时,会用行业通用资料兜底回答,并标注「非本店资料」;行业通用资料库改成每天自动更新。",
      },
      {
        get cabin() {
          return getPersona().title;
        },
        get text() {
          return `${getPersona().title}直接生成视频时会带上口播和你自己的声音,写的脚本也更贴合你选的调性。`;
        },
      },
      {
        cabin: "建站舱",
        text: "建站舱资料清单每一行都能直接点开编辑,案例、卖点、价格会自动帮你预填;页面结构的排序更稳定,不会自己跳来跳去。",
      },
      {
        cabin: "套餐",
        text: "开通或续费套餐,额度按你买的周期数发放;按年付费默认给一个周期的额度,页面上看得到到期时间和总额度。",
      },
      {
        cabin: "系统",
        text: "视频草稿失败满 7 天、或放着 30 天没动会自动清理;数字人视频如果实际出的片比预估短,差价会自动退给你。",
      },
    ],
  },
  {
    version: "1.1.1",
    date: "2026-09-24",
    kind: "minor",
    items: [
      {
        cabin: "视频舱",
        text: "自由创作可以传图了:传一张图当开头画面(还能再传一张当结尾),或传 1~4 张产品 / 场景图让 AI 照着拍。开头图和参考图二选一,不用自己拼提示词。",
      },
      {
        cabin: "视频舱",
        text: "新增「调性」三档:带货喊话 / 品牌克制 / 高奢静默。选高奢,片子不再满屏大字、不喊口号,镜头放慢、配音换成沉稳的声音;珠宝这类行业默认就是高奢。",
      },
      {
        cabin: "视频舱",
        text: "画面上要不要打大字,现在由 AI 导演按你这条片的内容来判断;片子详情里能看到它的理由。",
      },
      {
        cabin: "视频舱",
        text: "新增四种高端拍法:质感揭示、佩戴时刻、匠心工艺、礼赠时刻。选高奢调性时优先推荐它们。",
      },
      {
        cabin: "真人出镜",
        text: "新增样片墙:办公室开场、门店探店、车间巡视、节日祝福、招聘、行业知识、客户见证、手持产品讲解,8 条样片都能点「用这个拍」直接套用脚本。",
      },
      {
        cabin: "真人出镜",
        text: "新增「帮我写脚本」:一句话想法就能生成完整分镜脚本和口播,你改完再出片,不用自己写几百字提示词。",
      },
      {
        cabin: "真人出镜",
        text: "文案出片可以带上产品图,出镜人和产品同框。",
      },
      {
        cabin: "真人出镜",
        text: "「换我出镜」挪进「高级玩法」;参考视频超过 15 秒会提示切到「旗舰」档(最长 30 秒),不再默默失败。",
      },
      {
        cabin: "追爆仓",
        text: "新增出口「我来出镜拍这条」:把爆款的套路交给你本人出镜,原片的画面和台词一个都不会被搬走。",
      },
      {
        get cabin() {
          return getPersona().title;
        },
        get text() {
          return `${getPersona().title}能帮你写真人出镜脚本,也能直接选你的出镜人出片;下单前先告诉你出镜人的授权状态和价格。`;
        },
      },
      {
        cabin: "建站舱",
        text: "建站舱上线:选模板 → 资料采集(自动从你的知识库预填,每一项标出处)→ 内容编辑 → 域名绑定 → 发布,五步做一个官网。",
      },
      {
        cabin: "系统",
        text: "视频合成高峰时系统更稳,后台任务会自动腾出内存,不再出现整机卡顿。",
      },
      {
        cabin: "客服口径",
        text: "自由创作自己传的开头图不算出图费,视频仍按秒计价;真人出镜样片墙里的样片是虚拟人演示,画质与客户真实出片一致。",
      },
    ],
  },
  {
    version: "1.0.0",
    date: "2026-09-19",
    kind: "minor",
    items: [
      {
        cabin: "获客舱",
        text: "改成按产品走的四页:今日商机 / 去哪里找 / 线索库 / 我的生意。进来先看今天该打的几通电话,不用自己想去哪搜。",
      },
      {
        cabin: "获客舱",
        text: "推荐来自你自己的知识库与产品:系统先读公司知识库推出获客场景,再按场景挑该去哪个渠道找,行业包只做兜底。每条推荐都写明理由。",
      },
      {
        cabin: "获客舱",
        text: "计费改成「按接手的商机条数算」:推送、翻看、筛选全部免费,只有解锁联系方式才扣积分,价钱写在按钮上。",
      },
      {
        cabin: "追爆仓",
        text: "发现区换成「本周话题图谱」:当前话题的 30 天指数、周环比、关联词三组(业务 / 场景 / 事件),每个词都能直接跳去做内容或找商机。",
      },
      {
        cabin: "追爆仓",
        text: "换一个新话题词 16 积分,7 天内再看同一个词免费;行业默认话题一直免费。看不收钱,只有换词才收。",
      },
      {
        cabin: "行业知识库",
        text: "17 个行业包全部到齐(每个包 9 件:定位 / 话术 / 获客信号词典 / 内容选题词图 等)。选一次行业,全舱都懂你这一行的说法。",
      },
      {
        cabin: "行业知识库",
        text: "新增「家装维修与上门服务」行业包,本地上门服务这一行不再只能用通用兜底。",
      },
      {
        cabin: "漫剧工坊",
        text: "情报决策群卡墙与首页都能直接进漫剧工坊了(独立站,点卡新开一个页面)。",
      },
      {
        cabin: "客服口径",
        text: "GEO 雷达这一版起把每次检索如实记进积分明细。单次价格一分没变,变的是以前漏记的那部分现在记上了 —— 是结算归账,不是涨价。",
      },
      {
        cabin: "客服口径",
        text: "追爆仓换词收费只针对「换一个新词」:行业默认话题免费,换过的词 7 天内再看也免费。客户说「怎么点一下就扣钱」,先问他换的是不是新词。",
      },
    ],
  },
];

/** 当前构建的版本号 = 最新一条的 version(清单为空时返回空串,调用方按「不弹」处理)。 */
export const currentReleaseVersion = (): string => releaseNotes[0]?.version ?? "";
